// src/components/ForzarCambioPassword.js
// Pantalla bloqueante que aparece justo después del login cuando
// Acceso_Usuarios.PasswordTemporal = 1 (puesto por un reset administrativo).
// El usuario no entra al sistema hasta cambiar la contraseña temporal.
import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, TextField, Button,
    Alert, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LogoutIcon from '@mui/icons-material/Logout';
import { cambiarPasswordJWT } from '../api/endpoints';

export default function ForzarCambioPassword({ usuario, onCambiado, onLogout }) {
    const [actual, setActual] = useState('');
    const [nueva, setNueva] = useState('');
    const [repetir, setRepetir] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setError('');
        if (!actual.trim()) return setError('Ingresá la contraseña temporal que te dio el administrador.');
        if (nueva.length < 6) return setError('La contraseña nueva debe tener al menos 6 caracteres.');
        if (nueva !== repetir) return setError('Las contraseñas nuevas no coinciden.');
        if (nueva === actual) return setError('La contraseña nueva no puede ser igual a la temporal.');

        setEnviando(true);
        try {
            const resp = await cambiarPasswordJWT(actual.trim(), nueva);
            if (resp?.ok) {
                onCambiado?.();
            } else {
                setError(resp?.mensaje || 'No se pudo cambiar la contraseña.');
            }
        } catch {
            setError('Error de conexión al cambiar la contraseña.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
            p: 2,
        }}>
            <Card sx={{ maxWidth: 460, width: '100%', borderRadius: '4px' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <LockResetIcon sx={{ fontSize: 56, color: '#1565c0', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Cambiá tu contraseña
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Estás usando una contraseña temporal generada por un administrador.
                            Antes de entrar al sistema, definí una nueva.
                        </Typography>
                    </Box>

                    {usuario?.usuario && (
                        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                            Usuario: <b>{usuario.usuario}</b>
                        </Alert>
                    )}

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                            label="Contraseña temporal"
                            type={showPass ? 'text' : 'password'}
                            value={actual}
                            onChange={e => setActual(e.target.value)}
                            fullWidth
                            size="small"
                            autoFocus
                            disabled={enviando}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                                            {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Contraseña nueva"
                            type={showPass ? 'text' : 'password'}
                            value={nueva}
                            onChange={e => setNueva(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={enviando}
                            helperText="Mínimo 6 caracteres"
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>,
                            }}
                        />
                        <TextField
                            label="Repetir contraseña nueva"
                            type={showPass ? 'text' : 'password'}
                            value={repetir}
                            onChange={e => setRepetir(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={enviando}
                            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>,
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={submit}
                            disabled={enviando}
                            sx={{ mt: 1, textTransform: 'none', borderRadius: '4px' }}
                            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Guardar contraseña nueva
                        </Button>

                        <Button
                            variant="text"
                            onClick={onLogout}
                            disabled={enviando}
                            sx={{ textTransform: 'none', mt: 0.5, color: '#666' }}
                            startIcon={<LogoutIcon />}
                        >
                            Cerrar sesión
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
